import React, { useState, useEffect } from 'react';
import { Calendar, Search, Lock } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { Link, useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function Scadenze() {
  const navigate = useNavigate();
  const [deadlines, setDeadlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterDays, setFilterDays] = useState(30);
  const [currentUser, setCurrentUser] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  
  useEffect(() => {
    checkUserAccess();
  }, []);
  
  useEffect(() => {
    if (currentUser && !accessDenied) {
      fetchDeadlines();
    }
  }, [filterDays, currentUser, accessDenied]);
  
  const checkUserAccess = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }
      
      const res = await fetch(`${API_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) {
        navigate('/login');
        return;
      }
      
      const userData = await res.json();
      setCurrentUser(userData);
      
      // Only Admin and HSE Manager can see all deadlines
      if (userData.role === 'employee') {
        setAccessDenied(true);
      }
    } catch (err) {
      console.error('Error checking access:', err);
      navigate('/login');
    }
  };
  
  const fetchDeadlines = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/dashboard/upcoming-deadlines?days=${filterDays}`);
      const data = await res.json();
      setDeadlines(data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching deadlines:', err);
      setLoading(false);
    }
  };
  
  const getUrgencyBadgeClass = (level) => {
    if (level === 'expired' || level === 'critical') return 'badge-critical';
    if (level === 'superseded') return 'badge-secondary';
    if (level === 'warning') return 'badge-warning';
    if (level === 'soon') return 'badge-soon';
    return 'badge-ok';
  };
  
  const getUrgencyLabel = (days, level) => {
    if (level === 'superseded') return 'Superato';
    if (days < 0) return `Scaduto da ${Math.abs(days)} giorni`;
    if (days === 0) return 'Scade oggi';
    if (days === 1) return 'Scade domani';
    return `${days} giorni`;
  };
  
  if (accessDenied) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <Lock size={64} className="text-muted-foreground mb-4" />
        <h1 className="text-2xl font-bold mb-2">Accesso Limitato</h1>
        <p className="text-muted-foreground mb-4">
          Non hai i permessi per visualizzare tutte le scadenze.<br/>
          Puoi vedere le tue scadenze personali nella pagina "Il Mio Profilo".
        </p>
        <Link to="/profile" className="btn btn-primary">
          Vai al Mio Profilo
        </Link>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="loading" data-testid="loading">
        <p>Caricamento scadenze...</p>
      </div>
    );
  }
  
  return (
    <div data-testid="scadenze-page">
      <div className="page-header">
        <h1 className="page-title">Scadenze</h1>
        <p className="page-description">
          Visualizza e gestisci tutte le scadenze di compliance
        </p>
      </div>
      
      {/* Filters */}
      <div className="mb-6 flex gap-4">
        <div className="flex gap-2">
          {[7, 15, 30, 60, 90].map((days) => (
            <button
              key={days}
              className={`btn ${
                filterDays === days ? 'btn-primary' : 'btn-secondary'
              }`}
              onClick={() => setFilterDays(days)}
              data-testid={`filter-${days}-days`}
            >
              {days} giorni
            </button>
          ))}
        </div>
      </div>
      
      {/* Deadlines Table */}
      {deadlines.length === 0 ? (
        <div className="empty-state">
          <Calendar className="empty-state-icon" />
          <p>Nessuna scadenza nei prossimi {filterDays} giorni</p>
        </div>
      ) : (
        <div className="card">
          <div className="table-container">
            <table data-testid="deadlines-table">
              <thead>
                <tr>
                  <th>Urgenza</th>
                  <th>Tipo</th>
                  <th>Titolo</th>
                  <th>Dipendente</th>
                  <th>Data Scadenza</th>
                  <th>Stato</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {deadlines.map((item) => (
                  <tr key={item._id} data-testid="deadline-row">
                    <td>
                      <span className={`badge ${getUrgencyBadgeClass(item.urgency_level)}`}>
                        {getUrgencyLabel(item.days_remaining, item.urgency_level)}
                      </span>
                    </td>
                    <td>
                      <span className="badge badge-secondary">{item.tipo}</span>
                    </td>
                    <td className="font-medium">{item.titolo}</td>
                    <td>
                      <Link 
                        to={`/employees/${item.employee_id}`}
                        className="text-primary hover:underline"
                      >
                        {item.employee_nome} {item.employee_cognome}
                      </Link>
                    </td>
                    <td>
                      {format(new Date(item.data_scadenza), 'dd/MM/yyyy', { locale: it })}
                    </td>
                    <td>
                      <span className="badge badge-ok">{item.stato}</span>
                    </td>
                    <td>
                      <Link 
                        to={`/employees/${item.employee_id}`}
                        className="btn btn-secondary btn-sm"
                        data-testid="view-employee"
                      >
                        Dettagli
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default Scadenze;